import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { isNil, omit } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  ApiError,
  ImmunizationTypeEnum,
  ProviderCredentialingImmunization,
  ProviderCredentialingInsurances,
  ProviderCredentialingMisc,
  ProviderService,
} from '../../../api';
import { Card } from '../../../app/components/Card';
import {
  FORM_NAME as IMMUNIZATION_FORM_NAME,
  ImmunizationFormGroup,
} from '../../../app/components/ImmunizationFormGroup';
import {
  FORM_NAME as INSURANCE_FORM_NAME,
  InsuranceFormGroup,
} from '../../../app/components/InsuranceFormGroup';
import {
  FORM_NAME as MISCELLANEOUS_FORM_NAME,
  MiscellaneousFormGroup,
} from '../../../app/components/MiscellaneousFormGroup';
import { ServerErrorsAlert } from '../../../app/components/ServerErrorsAlert';
import { UnsavedChangesFormPrompt } from '../../../app/components/UnsavedChangesFormPrompt';
import { FormSubmitContext } from '../../../app/contexts/FormSubmitContext';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';
import { handleApiError } from '../../../app/utils/errorhandling';
import {
  ShowFieldErrors,
  generateShowFieldErrors,
  generateZEnumFromEnum,
} from '../../../utils/forms';
import { ProviderProfileCreateOutletContext } from '../../Registration/ProviderProfileCreate';
import { ProviderOtherInfoData, ProviderOtherInfoFormData } from './models';
import { ProviderFormContainer } from './ProviderFormContainer';

const validationSchema = z.object({
  [INSURANCE_FORM_NAME]: z.array(z.object({ file: z.string().url() })).optional(),
  [IMMUNIZATION_FORM_NAME]: z
    .array(
      z
        .object({
          type: generateZEnumFromEnum(ImmunizationTypeEnum),
          issue_date: z.union([
            z.null(),
            z.string().length(0),
            z.coerce.date().transform((val: Date) => {
              return new Date(val.valueOf() + val.getTimezoneOffset() * 60 * 1000);
            }),
          ]),
          expiration_date: z.union([
            z.null(),
            z.string().length(0),
            z.coerce.date().transform((val: Date) => {
              return new Date(val.valueOf() + val.getTimezoneOffset() * 60 * 1000);
            }),
          ]),
          exempt: z.boolean(),
          file__file: z.string().url(),
        })
        .refine(
          (data) => {
            if (isNil(data.issue_date)) return true;

            return data.issue_date < new Date();
          },
          {
            message: 'Date can`t be in the future',
            path: ['issue_date'],
          }
        )
        .refine(
          (data) => {
            if (isNil(data.expiration_date) || isNil(data.issue_date)) return true;

            return data.expiration_date > data.issue_date;
          },
          {
            message: 'Expiration date must be after Date Given',
            path: ['expiration_date'],
          }
        )
    )
    .optional(),
  [MISCELLANEOUS_FORM_NAME]: z
    .array(
      z
        .object({
          title: z.string(),
          issue_date: z.union([
            z.null(),
            z.string().length(0),
            z.coerce.date().transform((val: Date) => {
              return new Date(val.valueOf() + val.getTimezoneOffset() * 60 * 1000);
            }),
          ]),
          expiration_date: z.union([
            z.null(),
            z.string().length(0),
            z.coerce.date().transform((val: Date) => {
              return new Date(val.valueOf() + val.getTimezoneOffset() * 60 * 1000);
            }),
          ]),
          file__file: z.string().url(),
        })
        .refine(
          (data) => {
            if (isNil(data.issue_date)) return true;

            return data.issue_date < new Date();
          },
          {
            message: 'Date can`t be in the future',
            path: ['issue_date'],
          }
        )
        .refine(
          (data) => {
            if (isNil(data.expiration_date) || isNil(data.issue_date)) return true;

            return data.expiration_date > data.issue_date;
          },
          {
            message: 'Expiration date must be after Date Given',
            path: ['expiration_date'],
          }
        )
    )
    .optional(),
});

export const ProviderOtherInfoForm = () => {
  useDocumentTitle('Provider Other Info');
  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const { formRef, onSuccess } = useOutletContext<ProviderProfileCreateOutletContext>() ?? {};
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<ShowFieldErrors>({});
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const queryClient = useQueryClient();

  const mutationProviderOtherUpdate = useMutation({
    mutationFn: async (data: ProviderOtherInfoData) => {
      await Promise.all([
        ProviderService.providerCurrentCredentialingInsuranceUpdate({
          requestBody: {
            [INSURANCE_FORM_NAME]: data[INSURANCE_FORM_NAME] ?? [],
          } as ProviderCredentialingInsurances,
        }),
        ProviderService.providerCurrentCredentialingImmunizationUpdate({
          requestBody: {
            [IMMUNIZATION_FORM_NAME]: data[IMMUNIZATION_FORM_NAME] ?? [],
          } as ProviderCredentialingImmunization,
        }),
        ProviderService.providerCurrentCredentialingMiscUpdate({
          requestBody: {
            [MISCELLANEOUS_FORM_NAME]: data[MISCELLANEOUS_FORM_NAME] ?? [],
          } as ProviderCredentialingMisc,
        }),
      ]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(providerQueryKeys.all());
    },
  });

  const {
    clearErrors,
    control,
    handleSubmit,
    reset,
    register,
    formState: { isDirty, isSubmitSuccessful, errors },
  } = useForm({ resolver: zodResolver(validationSchema) });

  const [{ data: insurance }, { data: immunizations }, { data: miscellaneous }] = useQueries({
    queries: [
      {
        queryKey: providerQueryKeys.detailCredentialingInsurance(),
        queryFn: () => ProviderService.providerCurrentCredentialingInsuranceRetrieve(),
      },
      {
        queryKey: providerQueryKeys.detailCredentialingImmunization(),
        queryFn: () => ProviderService.providerCurrentCredentialingImmunizationRetrieve(),
      },
      {
        queryKey: providerQueryKeys.detailCredentialingMisc(),
        queryFn: () => ProviderService.providerCurrentCredentialingMiscRetrieve(),
      },
    ],
  });

  useEffect(() => {
    reset({
      [INSURANCE_FORM_NAME]: insurance?.[INSURANCE_FORM_NAME] ?? [],
      [IMMUNIZATION_FORM_NAME]: (immunizations?.[IMMUNIZATION_FORM_NAME] ?? []).map((item) => ({
        ...item,
        file__file: item.file?.file,
      })),
      [MISCELLANEOUS_FORM_NAME]: (miscellaneous?.[MISCELLANEOUS_FORM_NAME] ?? []).map((item) => ({
        ...item,
        file__file: item.file?.file,
      })),
    });
  }, [insurance, immunizations, miscellaneous, reset]);

  const isDisabled = useMemo(() => !isSubmitEnabled, [isSubmitEnabled]);

  const handleSubmitClick = useCallback(() => {
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
  }, [clearErrors]);

  const onSubmit = useCallback(
    async (data: ProviderOtherInfoFormData) => {
      try {
        await mutationProviderOtherUpdate.mutateAsync({
          ...data,
          immunizations: (data[IMMUNIZATION_FORM_NAME] ?? []).map((item) => ({
            ...omit(item, ['file__file']),
            issue_date: item.issue_date ? format(item.issue_date, 'yyyy-MM-dd') : null,
            expiration_date: item.expiration_date
              ? format(item.expiration_date, 'yyyy-MM-dd')
              : null,
            file: { file: item.file__file },
          })),
          miscellaneous: (data[MISCELLANEOUS_FORM_NAME] ?? []).map((item) => ({
            ...omit(item, ['file__file']),
            issue_date: item.issue_date ? format(item.issue_date, 'yyyy-MM-dd') : null,
            expiration_date: item.expiration_date
              ? format(item.expiration_date, 'yyyy-MM-dd')
              : null,
            file: { file: item.file__file },
          })),
        });

        toast.success('Provider Other Data Updated Successfully');
        onSuccess?.();
      } catch (errors) {
        if (errors.status === 400) {
          const fieldErrors = omit(errors.body, ['non_field_errors']);
          setServerErrors(errors);
          setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
          setShowGlobalServerErrors(true);
        }
        handleApiError(errors);
      }
    },
    [onSuccess, mutationProviderOtherUpdate]
  );

  return (
    <Card className="p-4">
      <h2 className="border-bottom pb-2 mb-4">Other</h2>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <InsuranceFormGroup
          control={control}
          serverErrors={serverErrors}
          showServerFieldErrors={showServerFieldErrors}
          errors={errors}
          validationSchema={validationSchema}
        />
        <ImmunizationFormGroup
          control={control}
          serverErrors={serverErrors}
          showServerFieldErrors={showServerFieldErrors}
          setShowServerFieldErrors={setShowServerFieldErrors}
          errors={errors}
          register={register}
          validationSchema={validationSchema}
        />
        <MiscellaneousFormGroup
          control={control}
          serverErrors={serverErrors}
          showServerFieldErrors={showServerFieldErrors}
          setShowServerFieldErrors={setShowServerFieldErrors}
          errors={errors}
          register={register}
          validationSchema={validationSchema}
        />
        <Row>
          <Col md={12}>
            <Button
              className="float-end"
              variant="primary"
              type="submit"
              disabled={isDisabled}
              onClick={handleSubmitClick}
            >
              Save Changes
            </Button>
          </Col>
        </Row>
        <ServerErrorsAlert
          errors={serverErrors}
          showErrors={showGlobalServerErrors}
          clearErrors={() => setShowGlobalServerErrors(false)}
        />
      </Form>
      <UnsavedChangesFormPrompt hasUnsavedChanges={isDirty && !isSubmitSuccessful} />
    </Card>
  );
};

export const ProviderOtherInfo = () => {
  return (
    <ProviderFormContainer>
      <ProviderOtherInfoForm />
    </ProviderFormContainer>
  );
};
